import {BOOLEAN_FEATURE_FLAGS} from "../constants/featureFlags";
import type {SmsProvider, TenantFeatureConfig, TenantUpdateRequest} from "../types/tenant";
import type {TernaryFlagValue} from "../components/feature-flags/TernaryFeatureFlag";
import {storedFlagValue} from "./featureFlagResolution";

/** The tenant fields saved together by one general update request. */
export type GeneralForm = {
    name: string;
    notes: string;
    administratorEmployeeId: string;
    /** Stored overrides (`null` = inherit), not the resolved values the detail endpoint returns. */
    featureConfig: TenantFeatureConfig;
    blockList: string[];
    assignedSupportEngineerIds: string[];
};

export type GeneralChange =
        | {kind: "name"; from: string; to: string}
        | {kind: "notes"}
        | {kind: "administrator"; fromId: string; toId: string}
        | {kind: "flag"; flag: string; from: TernaryFlagValue; to: TernaryFlagValue}
        | {kind: "smsProvider"; from: SmsProvider | undefined; to: SmsProvider | undefined}
        | {kind: "blocklistAdded"; entry: string}
        | {kind: "blocklistRemoved"; entry: string}
        | {kind: "engineerAdded"; employeeId: string}
        | {kind: "engineerRemoved"; employeeId: string};

function cleanBlockList(entries: string[]): string[] {
    return entries.map((entry) => entry.trim()).filter(Boolean);
}

function smsProviderOf(config: TenantFeatureConfig): SmsProvider | undefined {
    return config.smsProvider === "AWS_SNS" || config.smsProvider === "IBASIS" ? config.smsProvider : undefined;
}

export function listGeneralChanges(original: GeneralForm, current: GeneralForm): GeneralChange[] {
    const changes: GeneralChange[] = [];
    if (original.name.trim() !== current.name.trim()) {
        changes.push({kind: "name", from: original.name.trim(), to: current.name.trim()});
    }
    if (original.notes.trim() !== current.notes.trim()) changes.push({kind: "notes"});
    if (original.administratorEmployeeId !== current.administratorEmployeeId) {
        changes.push({kind: "administrator", fromId: original.administratorEmployeeId, toId: current.administratorEmployeeId});
    }
    for (const flag of BOOLEAN_FEATURE_FLAGS) {
        const from = storedFlagValue(original.featureConfig, flag);
        const to = storedFlagValue(current.featureConfig, flag);
        if (from !== to) changes.push({kind: "flag", flag, from, to});
    }
    const fromProvider = smsProviderOf(original.featureConfig);
    const toProvider = smsProviderOf(current.featureConfig);
    if (fromProvider !== toProvider) changes.push({kind: "smsProvider", from: fromProvider, to: toProvider});

    const originalBlockList = cleanBlockList(original.blockList);
    const currentBlockList = cleanBlockList(current.blockList);
    for (const entry of currentBlockList) {
        if (!originalBlockList.includes(entry)) changes.push({kind: "blocklistAdded", entry});
    }
    for (const entry of originalBlockList) {
        if (!currentBlockList.includes(entry)) changes.push({kind: "blocklistRemoved", entry});
    }

    for (const employeeId of current.assignedSupportEngineerIds) {
        if (!original.assignedSupportEngineerIds.includes(employeeId)) changes.push({kind: "engineerAdded", employeeId});
    }
    for (const employeeId of original.assignedSupportEngineerIds) {
        if (!current.assignedSupportEngineerIds.includes(employeeId)) changes.push({kind: "engineerRemoved", employeeId});
    }
    return changes;
}

export function buildTenantUpdateRequest(form: GeneralForm, originalAdministratorId: string): TenantUpdateRequest {
    return {
        name: form.name.trim(),
        notes: form.notes.trim() || undefined,
        featureConfig: {...form.featureConfig},
        assignedSupportEngineerIds: [...form.assignedSupportEngineerIds],
        // An empty list (not an absent one) is what clears the stored entries; absence keeps them.
        blockList: cleanBlockList(form.blockList),
        administratorEmployeeId:
                form.administratorEmployeeId && form.administratorEmployeeId !== originalAdministratorId
                        ? form.administratorEmployeeId
                        : undefined,
    };
}
